import { useState, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import {
  importApi,
  PreviewEvent,
  ImportUser,
  ApiRequestError,
} from '@/lib/api';

export default function ImportPage() {
  const [events, setEvents] = useState<PreviewEvent[]>([]);
  const [users, setUsers] = useState<ImportUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    created: Array<{ id: string; summary: string }>;
    skipped: Array<{ summary: string; reason: string }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);
    setImportResult(null);

    try {
      const result = await importApi.preview(file);
      setEvents(result.events);
      setUsers(result.users);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to parse iCal file');
      }
      setEvents([]);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserAssignment = (uid: string, userId: string) => {
    setEvents(
      events.map((event) =>
        event.uid === uid ? { ...event, assignedUserId: userId } : event
      )
    );
  };

  const handleAssignAll = (userId: string) => {
    setEvents(events.map((event) => ({ ...event, assignedUserId: userId })));
  };

  const handleRemoveEvent = (uid: string) => {
    setEvents(events.filter((event) => event.uid !== uid));
  };

  const handleImport = async () => {
    // Filter events with user assignments and no conflicts
    const eventsToImport = events
      .filter((e) => e.assignedUserId && !e.hasConflict)
      .map((e) => ({
        uid: e.uid,
        summary: e.summary,
        description: e.description,
        startDate: e.startDate,
        endDate: e.endDate,
        assignedUserId: e.assignedUserId!,
      }));

    if (eventsToImport.length === 0) {
      setError('No valid events to import. Assign users and resolve conflicts first.');
      return;
    }

    setIsImporting(true);
    setError(null);

    try {
      const result = await importApi.confirm(eventsToImport);
      setImportResult(result);
      setEvents([]);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to import events');
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleReset = () => {
    setEvents([]);
    setUsers([]);
    setError(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validEventsCount = events.filter(
    (e) => e.assignedUserId && !e.hasConflict
  ).length;

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = format(parseISO(startDate), 'MMM d, yyyy');
    const end = format(parseISO(endDate), 'MMM d, yyyy');
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Import Calendar</h1>
        <p className="mt-1 text-sm text-gray-500">
          Import bookings from an iCal (.ics) file. Events will be created as
          approved bookings.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {importResult && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <h3 className="font-medium text-green-800 mb-2">Import Complete</h3>
          {importResult.created.length > 0 && (
            <p className="text-sm text-green-700">
              Created {importResult.created.length} booking(s):
              <ul className="list-disc list-inside mt-1">
                {importResult.created.map((b) => (
                  <li key={b.id}>{b.summary}</li>
                ))}
              </ul>
            </p>
          )}
          {importResult.skipped.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-amber-700">
                Skipped {importResult.skipped.length} event(s):
              </p>
              <ul className="list-disc list-inside mt-1 text-sm text-amber-600">
                {importResult.skipped.map((s, i) => (
                  <li key={i}>
                    {s.summary}: {s.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <button onClick={handleReset} className="btn-secondary mt-4">
            Import Another File
          </button>
        </div>
      )}

      {events.length === 0 && !importResult && (
        <div className="card p-6">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Upload iCal File
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Select an .ics file exported from Google Calendar, Apple Calendar,
              or other calendar apps.
            </p>
            <div className="mt-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".ics"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                className="btn-primary"
              >
                {isLoading ? 'Processing...' : 'Select File'}
              </button>
            </div>
          </div>
        </div>
      )}

      {events.length > 0 && (
        <>
          {/* Actions bar */}
          <div className="card p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {events.length} event(s) found, {validEventsCount} ready to import
                </span>
                <select
                  onChange={(e) => handleAssignAll(e.target.value)}
                  className="input text-sm w-auto"
                >
                  <option value="">Assign all to...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button onClick={handleReset} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={isImporting || validEventsCount === 0}
                  className="btn-primary disabled:opacity-50"
                >
                  {isImporting
                    ? 'Importing...'
                    : `Import ${validEventsCount} Event(s)`}
                </button>
              </div>
            </div>
          </div>

          {/* Events list */}
          <div className="card overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Event
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Dates
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Assign To
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map((event) => (
                  <tr
                    key={event.uid}
                    className={event.hasConflict ? 'bg-red-50' : ''}
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {event.summary}
                      </div>
                      {event.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {event.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDateRange(event.startDate, event.endDate)}
                    </td>
                    <td className="px-4 py-3">
                      {event.hasConflict ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Conflict
                        </span>
                      ) : event.assignedUserId ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Needs User
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={event.assignedUserId || ''}
                        onChange={(e) =>
                          handleUserAssignment(event.uid, e.target.value)
                        }
                        disabled={event.hasConflict}
                        className="input text-sm"
                      >
                        <option value="">Select user...</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRemoveEvent(event.uid)}
                        className="text-gray-400 hover:text-red-600"
                        title="Remove"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
